import { from, of, concat } from 'rxjs';
import assert from '@banking/ui/utils/assert';
import workflow from '@banking/ui/lib/workflow';
import { isCardHolder } from '@banking/ui/utils/card-settings-utils';
import { ActivateCardRequest, ActivateCardResponse, SetPinRequest, SetPinResponse } from '@square/dashboard-protos/models/squareup/bbdebit/cardmanagement';
import { convertExpiryDateToYearMonth } from '@banking/ui/utils/card/date';

// State
// =============================================================================

export let Screen = /*#__PURE__*/function (Screen) {
  Screen[Screen["None"] = 0] = "None";
  Screen[Screen["DETAILS"] = 1] = "DETAILS";
  Screen[Screen["CREATE_PIN"] = 2] = "CREATE_PIN";
  Screen[Screen["SUCCESS"] = 3] = "SUCCESS";
  return Screen;
}({});
export const INITIAL_STATE = {
  cardToken: '',
  confirmPinValue: '',
  confirmPinErrorMessage: '',
  createPinValue: '',
  createPinErrorMessage: '',
  createPinScreenErrorMessage: '',
  cvcValue: '',
  cvcErrorMessage: '',
  detailsErrorMessage: '',
  expiryDateValue: '',
  expiryDateErrorMessage: '',
  hasPendingActivation: false,
  isLoading: false,
  isOpen: false,
  screen: Screen.None,
  unitToken: ''
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["ActivateCardReceived"] = "ACTIVATE_CARD_RECEIVED";
  ActionType["ActivateCardRejected"] = "ACTIVATE_CARD_REJECTED";
  ActionType["IncrementScreen"] = "INCREMENT_SCREEN";
  ActionType["SetPinReceived"] = "SET_PIN_RECEIVED";
  ActionType["SetPinRejected"] = "SET_PIN_REJECTED";
  ActionType["ToggleLoading"] = "TOGGLE_LOADING";
  ActionType["ToggleModal"] = "TOGGLE_MODAL";
  ActionType["UserChangeConfirmPin"] = "USER_CHANGE_CONFIRM_PIN";
  ActionType["UserChangeCreatePin"] = "USER_CHANGE_CREATE_PIN";
  ActionType["UserChangeCvc"] = "USER_CHANGE_CVC";
  ActionType["UserChangeExpiryDate"] = "USER_CHANGE_EXPIRY_DATE";
  ActionType["UserClickSetPasscode"] = "USER_CLICK_SET_PASSCODE";
  ActionType["UserClickVerifyCardDetails"] = "USER_CLICK_VERIFY_CARD_DETAILS";
  return ActionType;
}({});
// Action Creators
// =============================================================================

export const activateCardReceivedAction = response => ({
  type: ActionType.ActivateCardReceived,
  response
});
export const activateCardRejectedAction = error => ({
  type: ActionType.ActivateCardRejected,
  error
});
export const incrementScreenAction = screen => ({
  type: ActionType.IncrementScreen,
  screen
});
export const setPinReceivedAction = response => ({
  type: ActionType.SetPinReceived,
  response
});
export const setPinRejectedAction = error => ({
  type: ActionType.SetPinRejected,
  error
});
export const toggleLoadingAction = value => ({
  type: ActionType.ToggleLoading,
  value
});
export const toggleModalAction = (isOpen, unitToken, cardToken) => ({
  type: ActionType.ToggleModal,
  isOpen,
  unitToken,
  cardToken
});
export const userChangeConfirmPinAction = value => ({
  type: ActionType.UserChangeConfirmPin,
  value
});
export const userChangeCreatePinAction = value => ({
  type: ActionType.UserChangeCreatePin,
  value
});
export const userChangeCvcAction = value => ({
  type: ActionType.UserChangeCvc,
  value
});
export const userChangeExpiryDateAction = value => ({
  type: ActionType.UserChangeExpiryDate,
  value
});
export const userClickSetPasscodeAction = () => ({
  type: ActionType.UserClickSetPasscode
});
export const userClickVerifyCardDetailsAction = () => ({
  type: ActionType.UserClickVerifyCardDetails
});

// Update
// =============================================================================

function handleActivateCardReceived(context, state, action) {
  const {
    error,
    card
  } = action.response;
  const {
    ActivateCardError
  } = ActivateCardResponse;
  if (!error && card) {
    return [{
      ...state,
      detailsErrorMessage: ''
    }, () => incrementScreenAction(card.pinIsSet || !isCardHolder(card) ? Screen.SUCCESS : Screen.CREATE_PIN)];
  }
  switch (error) {
    case ActivateCardError.ACTIVATE_CARD_ERROR_INVALID_CVV_OR_EXPIRATION:
      {
        const detailsErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.details.error.invalidData');
        return [{
          ...state,
          detailsErrorMessage
        }, null];
      }
    default:
      {
        const detailsErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
        context.sentry.captureException(`ActivateCardResponse.ActivateCardError: ${error}`);
        return [{
          ...state,
          detailsErrorMessage
        }, null];
      }
  }
}
function handleActivateCardRejected(context, state, action) {
  const detailsErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
  context.sentry.captureException(action.error);
  return [{
    ...state,
    detailsErrorMessage
  }, null];
}
function handleIncrementScreen(state, action) {
  const {
    screen
  } = action;
  return [{
    ...state,
    screen
  }, null];
}
function handleSetPinReceived(context, state, action) {
  const {
    error
  } = action.response;
  if (!error) {
    return [{
      ...state,
      createPinScreenErrorMessage: ''
    }, () => incrementScreenAction(Screen.SUCCESS)];
  }
  const {
    SetPinError
  } = SetPinResponse;
  switch (error) {
    case SetPinError.SET_PIN_ERROR_WEAK_PIN:
      {
        const createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.weak');
        return [{
          ...state,
          createPinScreenErrorMessage
        }, null];
      }
    default:
      {
        const createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
        context.sentry.captureException(`SetPinResponse.SetPinError: ${error}`);
        return [{
          ...state,
          createPinScreenErrorMessage
        }, null];
      }
  }
}
function handleSetPinRejected(context, state, action) {
  const createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.error.server');
  context.sentry.captureException(action.error);
  return [{
    ...state,
    createPinScreenErrorMessage
  }, null];
}
function handleToggleLoading(state, action) {
  const {
    value
  } = action;
  const isLoading = value ?? !state.isLoading;
  return [{
    ...state,
    isLoading
  }, null];
}
function handleToggleModal(state, action) {
  const {
    isOpen,
    unitToken,
    cardToken
  } = action;
  if (isOpen) {
    assert(unitToken && unitToken.length > 0);
    assert(cardToken && cardToken.length > 0);
    return [{
      ...state,
      screen: Screen.DETAILS,
      isOpen,
      unitToken,
      cardToken
    }, null];
  }
  // reset state on modal close
  return [INITIAL_STATE, null];
}
function handleUserChangeConfirmPin(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    confirmPinValue: value,
    confirmPinErrorMessage: '',
    createPinScreenErrorMessage: ''
  }, null];
}
function handleUserChangeCreatePin(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    createPinValue: value,
    createPinErrorMessage: '',
    createPinScreenErrorMessage: ''
  }, null];
}
function handleUserChangeCvc(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    cvcValue: value,
    cvcErrorMessage: '',
    detailsErrorMessage: ''
  }, null];
}
function handleUserChangeExpiryDate(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    expiryDateValue: value,
    expiryDateErrorMessage: '',
    detailsErrorMessage: ''
  }, null];
}
function handleUserClickSetPasscode(context, state) {
  let createPinErrorMessage = '';
  let confirmPinErrorMessage = '';
  let createPinScreenErrorMessage = '';
  if (state.createPinValue.length === 0) {
    createPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.create');
  } else if (state.createPinValue.length < 4) {
    createPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.validInput');
  }
  if (state.confirmPinValue.length === 0) {
    confirmPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.confirm');
  } else if (state.confirmPinValue.length < 4) {
    confirmPinErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.validInput');
  }
  if (state.createPinValue !== state.confirmPinValue) {
    createPinScreenErrorMessage = context.intl.t('banking.checking.cardSettings.pin.error.notMatch');
  }
  if (createPinErrorMessage || confirmPinErrorMessage || createPinScreenErrorMessage) {
    return [{
      ...state,
      createPinErrorMessage,
      confirmPinErrorMessage,
      createPinScreenErrorMessage
    }, null];
  }
  assert(state.cardToken, 'Missing card token');
  const work = () => {
    const setPinResponse = context.cardManagementService.client.setPin(SetPinRequest.create({
      cardToken: state.cardToken,
      pin: state.createPinValue
    })).then(setPinReceivedAction, setPinRejectedAction);
    return concat(of(toggleLoadingAction(true)), from(setPinResponse), of(toggleLoadingAction(false)));
  };
  return [state, work];
}
function handleUserClickVerifyCardDetails(context, state) {
  let expiryDateErrorMessage = '';
  let cvcErrorMessage = '';
  if (state.expiryDateValue.length === 0) {
    expiryDateErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.details.expiryDate.error.empty');
  }
  if (state.cvcValue.length === 0) {
    cvcErrorMessage = context.intl.t('banking.checking.cardSettings.cardActivation.details.cvc.error.empty');
  }
  if (expiryDateErrorMessage || cvcErrorMessage) {
    return [{
      ...state,
      expiryDateErrorMessage,
      cvcErrorMessage
    }, null];
  }
  const work = () => {
    const cardToken = state.cardToken;
    const cvv = state.cvcValue;
    const expiration = convertExpiryDateToYearMonth(state.expiryDateValue);
    const request = ActivateCardRequest.create({
      cardToken,
      cvv,
      expiration
    });
    const activateCardResponse = context.cardManagementService.client.activateCard(request).then(activateCardReceivedAction, activateCardRejectedAction);
    return concat(of(toggleLoadingAction(true)), from(activateCardResponse), of(toggleLoadingAction(false)));
  };
  return [state, work];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.ActivateCardReceived:
      return handleActivateCardReceived(context, state, action);
    case ActionType.ActivateCardRejected:
      return handleActivateCardRejected(context, state, action);
    case ActionType.IncrementScreen:
      return handleIncrementScreen(state, action);
    case ActionType.SetPinReceived:
      return handleSetPinReceived(context, state, action);
    case ActionType.SetPinRejected:
      return handleSetPinRejected(context, state, action);
    case ActionType.ToggleLoading:
      return handleToggleLoading(state, action);
    case ActionType.ToggleModal:
      return handleToggleModal(state, action);
    case ActionType.UserChangeConfirmPin:
      return handleUserChangeConfirmPin(state, action);
    case ActionType.UserChangeCreatePin:
      return handleUserChangeCreatePin(state, action);
    case ActionType.UserChangeCvc:
      return handleUserChangeCvc(state, action);
    case ActionType.UserChangeExpiryDate:
      return handleUserChangeExpiryDate(state, action);
    case ActionType.UserClickSetPasscode:
      return handleUserClickSetPasscode(context, state);
    case ActionType.UserClickVerifyCardDetails:
      return handleUserClickVerifyCardDetails(context, state);
  }
};
const debitCardActivation = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default debitCardActivation;